"use client";

/**
 * TablePickerModal.tsx
 *
 * A multi-step modal for completing a table reservation.
 *
 * Flow:
 *  Step 1: User selects an available table from the grid.
 *  Step 2: User enters their contact details (Name, Phone, etc.).
 *  Step 3: Submission triggers and modal closes on success.
 */

import React, { useState } from "react";
import { useIsPreviewMode } from "@/hooks/useIsPreviewMode";
import { motion, AnimatePresence } from "framer-motion";
import {
  X,
  User,
  Phone,
  Mail,
  MessageSquare,
  ArrowLeft,
  Check,
} from "lucide-react";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface Table {
  id?: string;
  uuid?: string;
  table_id?: string;
  table_number?: string | number;
  name?: string;
  table_name?: string;
  number?: number;
  capacity?: number;
  seats?: number;
}

interface TablePickerModalProps {
  tables: Table[];
  isLoading: boolean;
  onSelect: (tableId: string, details: any) => void;
  onClose: () => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const TablePickerModal = ({
  tables,
  isLoading,
  onSelect,
  onClose,
}: TablePickerModalProps) => {
  const isPreview = useIsPreviewMode();
  const [step, setStep] = useState(1);
  const [selectedTableId, setSelectedTableId] = useState<string | null>(null);
  const [customerDetails, setCustomerDetails] = useState({
    customer_name: "",
    customer_email: "",
    customer_phone: "",
    special_requests: "",
  });

  const handleTablePick = (id: string) => {
    setSelectedTableId(id);
    setStep(2); // Move to details step
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    setCustomerDetails((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isPreview) return;
    if (selectedTableId) {
      onSelect(selectedTableId, customerDetails);
    }
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70 px-4 backdrop-blur-sm">
      <motion.div
        initial={{ opacity: 0, scale: 0.9, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        className="bg-white rounded-lg p-6 md:p-10 max-w-xl w-full shadow-2xl relative border-2 border-[#c59d5f] overflow-hidden"
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-black transition-colors z-20"
        >
          <X size={24} />
        </button>

        <AnimatePresence mode="wait">
          {/* ── STEP 1: Table Selection ── */}
          {step === 1 && (
            <motion.div
              key="step1"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              className="relative z-10"
            >
              <h3 className="font-cormorant text-4xl font-bold mb-2 text-center text-[#1a1a1a]">
                Select Your Table
              </h3>
              <p className="text-center text-sm font-inter text-[#9b9b9b] mb-8">
                Choose one of the {tables.length} available tables for your
                requested time.
              </p>

              <div className="grid grid-cols-2 xs:grid-cols-3 sm:grid-cols-4 gap-4 max-h-[50vh] overflow-y-auto pr-2 p-1 custom-scrollbar">
                {tables.map((t, idx) => {
                  const id = String(
                    t.id || t.uuid || t.table_id || t.table_number || idx + 1,
                  );
                  const displayName =
                    t.table_number || t.name || t.table_name || `T-${idx + 1}`;
                  const capacity = t.capacity || t.seats;

                  return (
                    <button
                      key={id}
                      onClick={() => handleTablePick(id)}
                      className="aspect-square flex flex-col items-center justify-center p-4 border-2 border-[#f0f0f0] bg-[#fafafa] rounded-xl hover:border-[#c59d5f] hover:bg-white hover:shadow-lg transition-all duration-300 group"
                    >
                      <span className="font-inter font-bold text-2xl text-[#222] group-hover:text-[#c59d5f] transition-colors">
                        {displayName}
                      </span>
                      {capacity && (
                        <span className="text-[10px] font-bold uppercase tracking-widest text-[#999] mt-2">
                          Seats: {capacity}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </motion.div>
          )}

          {/* ── STEP 2: Customer Details ── */}
          {step === 2 && (
            <motion.div
              key="step2"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="relative z-10"
            >
              <button
                onClick={() => setStep(1)}
                className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-[#c59d5f] mb-6 hover:text-black transition-colors"
              >
                <ArrowLeft size={16} /> Back to Tables
              </button>

              <h3 className="font-cormorant text-4xl font-bold mb-2 text-center text-[#1a1a1a]">
                Complete Booking
              </h3>
              <p className="text-center text-sm font-inter text-[#9b9b9b] mb-8">
                Please provide your contact details to secure your reservation.
              </p>

              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="space-y-1">
                  <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#999] ml-1">
                    Full Name
                  </label>
                  <div className="relative">
                    <User
                      className="absolute left-4 top-1/2 -translate-y-1/2 text-[#c59d5f]"
                      size={18}
                    />
                    <input
                      required
                      name="customer_name"
                      placeholder="Jane Doe"
                      value={customerDetails.customer_name}
                      onChange={handleInputChange}
                      className="w-full h-14 pl-12 pr-4 bg-[#f9f9f9] border border-[#eee] rounded-lg text-sm focus:border-[#c59d5f] outline-none transition-colors"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div className="space-y-1">
                    <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#999] ml-1">
                      Phone Number
                    </label>
                    <div className="relative">
                      <Phone
                        className="absolute left-4 top-1/2 -translate-y-1/2 text-[#c59d5f]"
                        size={18}
                      />
                      <input
                        required
                        type="tel"
                        name="customer_phone"
                        placeholder="+1 234 567 890"
                        value={customerDetails.customer_phone}
                        onChange={handleInputChange}
                        className="w-full h-14 pl-12 pr-4 bg-[#f9f9f9] border border-[#eee] rounded-lg text-sm focus:border-[#c59d5f] outline-none transition-colors"
                      />
                    </div>
                  </div>
                  <div className="space-y-1">
                    <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#999] ml-1">
                      Email (Optional)
                    </label>
                    <div className="relative">
                      <Mail
                        className="absolute left-4 top-1/2 -translate-y-1/2 text-[#c59d5f]"
                        size={18}
                      />
                      <input
                        type="email"
                        name="customer_email"
                        placeholder="jane@example.com"
                        value={customerDetails.customer_email}
                        onChange={handleInputChange}
                        className="w-full h-14 pl-12 pr-4 bg-[#f9f9f9] border border-[#eee] rounded-lg text-sm focus:border-[#c59d5f] outline-none transition-colors"
                      />
                    </div>
                  </div>
                </div>

                <div className="space-y-1">
                  <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#999] ml-1">
                    Special Requests
                  </label>
                  <div className="relative">
                    <MessageSquare
                      className="absolute left-4 top-4 text-[#c59d5f]"
                      size={18}
                    />
                    <textarea
                      name="special_requests"
                      placeholder="e.g., Birthday celebration, window seat, allergies..."
                      value={customerDetails.special_requests}
                      onChange={handleInputChange}
                      rows={3}
                      className="w-full p-4 pl-12 bg-[#f9f9f9] border border-[#eee] rounded-lg text-sm focus:border-[#c59d5f] outline-none transition-colors resize-none"
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={isLoading || isPreview}
                  title={
                    isPreview
                      ? "Booking is disabled in preview mode"
                      : undefined
                  }
                  className="w-full h-16 bg-[#111] text-white font-bold uppercase tracking-[0.3em] text-xs rounded-lg hover:bg-[#c59d5f] transition-all flex items-center justify-center gap-2 group disabled:opacity-70"
                >
                  {isLoading ? (
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{
                        repeat: Infinity,
                        duration: 1,
                        ease: "linear",
                      }}
                      className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full"
                    />
                  ) : (
                    <>
                      Confirm Booking{" "}
                      <Check
                        size={18}
                        className="group-hover:scale-125 transition-transform"
                      />
                    </>
                  )}
                </button>
              </form>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Decorative background logo/pattern */}
        <div className="absolute -bottom-10 -right-10 opacity-[0.03] pointer-events-none">
          <Check size={200} />
        </div>
      </motion.div>

      <style jsx>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 4px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: #f1f1f1;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #c59d5f;
          border-radius: 10px;
        }
      `}</style>
    </div>
  );
};

export default TablePickerModal;
